import React from 'react';
import PropTypes from 'prop-types';

/**
 * 기본 카드 컴포넌트
 * 표준 그림자와 여백을 가진 카드 레이아웃
 */
export const Card = ({ 
  children, 
  title, 
  subtitle,
  action,
  className = '',
  padding = 'default', // 'none', 'sm', 'default', 'lg'
  shadow = 'default', // 'none', 'sm', 'default', 'lg'
  hover = false,
  onClick,
  selected = false,
  borderColor = null
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    default: 'p-4',
    lg: 'p-6'
  };

  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    default: 'shadow-lg',
    lg: 'shadow-xl'
  };

  const baseClasses = `bg-white rounded-3xl ${shadowClasses[shadow]} ${paddingClasses[padding]}`;
  const interactiveClasses = hover || onClick ? 'hover:shadow-xl transition-shadow duration-200' : '';
  const clickableClasses = onClick ? 'cursor-pointer' : '';
  const selectedClasses = selected ? 'ring-2 ring-primary border-transparent' : '';
  const borderClasses = borderColor ? `border-l-4 ${borderColor}` : '';

  return (
    <div 
      className={`${baseClasses} ${interactiveClasses} ${clickableClasses} ${selectedClasses} ${borderClasses} ${className}`}
      onClick={onClick}
    >
      {/* 카드 헤더 */}
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between">
          <div>
            {title && (
              <h3 className="text-[14px] font-bold text-gray-800">{title}</h3>
            )}
            {subtitle && (
              <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      
      {/* 카드 콘텐츠 */}
      {children}
    </div>
  );
};

/**
 * 압축형 카드 컴포넌트
 * 좌측 컬러 보더와 압축된 레이아웃을 가진 카드
 */
export const CompactCard = ({ 
  children, 
  title, 
  subtitle,
  action,
  className = '',
  variant = 'primary', // 'primary', 'secondary', 'info', 'success', 'warning', 'danger'
  onClick,
  showActionText = true,
  actionText = '← 돌아가기'
}) => {
  // variant별 스타일 정의
  const variantStyles = {
    primary: {
      border: 'border-primary',
      bg: 'bg-gray-50',
      hoverBg: 'hover:bg-gray-100',
      text: 'text-primary'
    },
    secondary: {
      border: 'border-gray-500',
      bg: 'bg-gray-50',
      hoverBg: 'hover:bg-gray-100',
      text: 'text-gray-700'
    },
    info: {
      border: 'border-blue-500',
      bg: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100',
      text: 'text-blue-600'
    },
    success: {
      border: 'border-success',
      bg: 'bg-green-50',
      hoverBg: 'hover:bg-green-100',
      text: 'text-success'
    },
    warning: {
      border: 'border-warning',
      bg: 'bg-yellow-50',
      hoverBg: 'hover:bg-yellow-100',
      text: 'text-warning-hover'
    },
    danger: {
      border: 'border-danger',
      bg: 'bg-red-50',
      hoverBg: 'hover:bg-red-100',
      text: 'text-danger'
    }
  };

  const styles = variantStyles[variant] || variantStyles.primary;

  return (
    <div 
      className={`${styles.bg} rounded border-l-4 ${styles.border} cursor-pointer p-2 ${styles.hoverBg} transition-colors ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          {title && (
            <h4 className="truncate text-sm font-medium text-gray-900">{title}</h4>
          )}
          {subtitle && (
            <p className="text-xs text-gray-600">{subtitle}</p>
          )}
        </div>
        {action && (
          <div className="ml-2 shrink-0">
            {action}
          </div>
        )}
      </div>
      
      {children}
      
      {showActionText && actionText && (
        <div className={`text-xs ${styles.text} mt-1`}>{actionText}</div>
      )}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
  padding: PropTypes.oneOf(['none', 'sm', 'default', 'lg']),
  shadow: PropTypes.oneOf(['none', 'sm', 'default', 'lg']),
  hover: PropTypes.bool,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  borderColor: PropTypes.string
};

CompactCard.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'danger']),
  onClick: PropTypes.func,
  showActionText: PropTypes.bool,
  actionText: PropTypes.string
}; 